import type { AttackSurfaceLicenseSupport } from './license.schemas';

export type AttackSurfaceSupportFields = {
  webApplicationSupport: AttackSurfaceLicenseSupport;
  externalNetworkSupport: AttackSurfaceLicenseSupport;
  aiApplicationSupport: AttackSurfaceLicenseSupport;
  internalNetworkSupport: AttackSurfaceLicenseSupport;
};

export const ATTACK_SURFACE_LICENSE_FLAG_KEYS = [
  'TERRA_PORTAL_DOWNLOAD_PAGE_ENABLED',
  'EXTERNAL_NETWORK_ENABLED',
  'INTERNAL_NETWORK_PENTEST_ENABLED',
] as const;

type AttackSurfaceFlagKey = (typeof ATTACK_SURFACE_LICENSE_FLAG_KEYS)[number];

type AttackSurfaceFlagMapping = {
  licenseField: keyof AttackSurfaceSupportFields;
  flagKey: AttackSurfaceFlagKey;
  supportWhenEnabled: AttackSurfaceLicenseSupport;
  supportWhenDisabled: AttackSurfaceLicenseSupport;
};

export const ATTACK_SURFACE_FLAG_MAPPINGS = [
  {
    licenseField: 'webApplicationSupport',
    flagKey: 'TERRA_PORTAL_DOWNLOAD_PAGE_ENABLED',
    supportWhenEnabled: 'SUPPORTED_WITH_TORCH',
    supportWhenDisabled: 'SUPPORTED',
  },
  {
    licenseField: 'externalNetworkSupport',
    flagKey: 'EXTERNAL_NETWORK_ENABLED',
    supportWhenEnabled: 'SUPPORTED',
    supportWhenDisabled: 'NOT_SUPPORTED',
  },
  {
    licenseField: 'internalNetworkSupport',
    flagKey: 'INTERNAL_NETWORK_PENTEST_ENABLED',
    supportWhenEnabled: 'SUPPORTED',
    supportWhenDisabled: 'NOT_SUPPORTED',
  },
] as const satisfies ReadonlyArray<AttackSurfaceFlagMapping>;

const toBooleanFlagValue = (value: unknown): boolean => value === true;

const resolveTenantFlagEnabled = ({
  flagKey,
  tenantId,
  configDefaultByFlagKey,
  tenantRuleByFlagKey,
}: {
  flagKey: AttackSurfaceFlagKey;
  tenantId: string;
  configDefaultByFlagKey: ReadonlyMap<string, unknown>;
  tenantRuleByFlagKey: ReadonlyMap<string, unknown>;
}): boolean => {
  const tenantRuleKey = `${flagKey}:${tenantId}`;

  if (tenantRuleByFlagKey.has(tenantRuleKey)) {
    return toBooleanFlagValue(tenantRuleByFlagKey.get(tenantRuleKey));
  }

  if (configDefaultByFlagKey.has(flagKey)) {
    return toBooleanFlagValue(configDefaultByFlagKey.get(flagKey));
  }

  return false;
};

export const resolveAttackSurfaceSupportForTenant = ({
  tenantId,
  configDefaultByFlagKey,
  tenantRuleByFlagKey,
}: {
  tenantId: string;
  configDefaultByFlagKey: ReadonlyMap<string, unknown>;
  tenantRuleByFlagKey: ReadonlyMap<string, unknown>;
}): AttackSurfaceSupportFields =>
  ({
    ...(Object.fromEntries(
      ATTACK_SURFACE_FLAG_MAPPINGS.map((mapping) => {
        const isEnabled = resolveTenantFlagEnabled({
          flagKey: mapping.flagKey,
          tenantId,
          configDefaultByFlagKey,
          tenantRuleByFlagKey,
        });

        return [
          mapping.licenseField,
          isEnabled ? mapping.supportWhenEnabled : mapping.supportWhenDisabled,
        ];
      }),
    ) as Omit<AttackSurfaceSupportFields, 'aiApplicationSupport'>),
    aiApplicationSupport: 'SUPPORTED',
  }) as AttackSurfaceSupportFields;

export const toMongoAttackSurfaceSupportFields = ({
  webApplicationSupport,
  externalNetworkSupport,
  aiApplicationSupport,
  internalNetworkSupport,
}: AttackSurfaceSupportFields) => ({
  web_application_support: webApplicationSupport,
  external_network_support: externalNetworkSupport,
  ai_application_support: aiApplicationSupport,
  internal_network_support: internalNetworkSupport,
});

const SURFACE_ENABLEMENT_FLAG_MAPPINGS = ATTACK_SURFACE_FLAG_MAPPINGS.filter(
  (mapping) => mapping.flagKey !== 'TERRA_PORTAL_DOWNLOAD_PAGE_ENABLED',
);

export const isAttackSurfaceEnabled = (
  support: AttackSurfaceLicenseSupport,
): boolean => support === 'SUPPORTED' || support === 'SUPPORTED_WITH_TORCH';

export const isAttackSurfaceTorchEnabled = (
  support: AttackSurfaceLicenseSupport,
): boolean => support === 'SUPPORTED_WITH_TORCH';

export const hasTorchLicenseSupport = (
  support: AttackSurfaceSupportFields,
): boolean => Object.values(support).some(isAttackSurfaceTorchEnabled);

export const resolveEffectiveTorchEnabled = ({
  torchEnabled,
  tenantDefaultTorchEnabled,
}: {
  torchEnabled: boolean | null;
  tenantDefaultTorchEnabled: boolean;
}): boolean => torchEnabled ?? tenantDefaultTorchEnabled;

export const resolveTenantDefaultTorchEnabled = ({
  applicationKind,
  license,
}: {
  applicationKind: 'AI_APPLICATION' | 'WEB_APPLICATION';
  license: Pick<
    AttackSurfaceSupportFields,
    'webApplicationSupport' | 'aiApplicationSupport'
  > | null;
}): boolean => {
  if (!license) {
    return false;
  }

  const support =
    applicationKind === 'AI_APPLICATION'
      ? license.aiApplicationSupport
      : license.webApplicationSupport;

  return isAttackSurfaceTorchEnabled(support);
};

export const resolveFeatureFlagSuggestionsFromAttackSurfaceSupport = (
  support: AttackSurfaceSupportFields,
): Record<AttackSurfaceFlagKey, boolean> => {
  const hasTorchSupport = hasTorchLicenseSupport(support);

  const suggestions = Object.fromEntries(
    SURFACE_ENABLEMENT_FLAG_MAPPINGS.map((mapping) => [
      mapping.flagKey,
      isAttackSurfaceEnabled(support[mapping.licenseField]),
    ]),
  ) as Record<
    Exclude<AttackSurfaceFlagKey, 'TERRA_PORTAL_DOWNLOAD_PAGE_ENABLED'>,
    boolean
  >;

  return {
    TERRA_PORTAL_DOWNLOAD_PAGE_ENABLED: hasTorchSupport,
    ...suggestions,
  };
};
